import type { PhysicalComponent } from '../services/garageService';

export type PhysicalComponentRow = { id: string; label: string; value: string };
export type PhysicalComponentSection = { title: string; rows: PhysicalComponentRow[] };

const asRecord = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  return {};
};

const isInternalKey = (key: string): boolean => {
  const n = key.split('.').pop()?.toLowerCase() || key.toLowerCase();
  return n === 'id' || n === '_id' || n === 'ref';
};

const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value) && Math.abs(value) >= 1000) return value.toLocaleString('es-ES');
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  const s = `${value}`.trim();
  return s || undefined;
};

const firstDefined = (...candidates: unknown[]): unknown => {
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    if (typeof c === 'string' && c.trim() === '') continue;
    return c;
  }
  return undefined;
};

const pickRaw = (raw: Record<string, unknown>, keys: string[]): unknown => {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null && `${v}`.trim() !== '') return v;
  }
  return undefined;
};

const normKey = (k: string) => k.replace(/[_-]/g, '').toLowerCase();

/** Bloque ui.byType con nombre camel/Pascal o guiones. */
const getByTypeSection = (byType: Record<string, unknown>, canonical: string): Record<string, unknown> => {
  const want = normKey(canonical);
  const hit = Object.keys(byType).find((k) => normKey(k) === want);
  return hit ? asRecord(byType[hit]) : {};
};

const bandSensitivity = (radarBlock: Record<string, unknown>, band: string): unknown => {
  const want = normKey(band);
  const hit = Object.keys(radarBlock).find((k) => normKey(k) === want);
  if (!hit) return undefined;
  return asRecord(radarBlock[hit]).sensitivity;
};

/** Etiquetas conocidas para claves de stats (resto se humaniza). */
const STAT_LABELS: Record<string, string> = {
  powerConsumption: 'Consumo de energía',
  power: 'Consumo de energía',
  powerSegment: 'Segmento energía',
  mass: 'Masa',
  coolingConsumption: 'Consumo de enfriamiento',
  emSignature: 'Firma EM (máx.)',
  irSignature: 'Firma IR',
  health: 'Salud',
  distortionMax: 'Distorsión máx.',
  distortionShutdownDamage: 'Daño apagado distorsión',
  damageThreshold: 'Umbral de daño',
  damageThresholdMin: 'Daño mín. distorsión',
  damageThresholdMax: 'Daño máx. distorsión',
  distortionDecayDelay: 'Retraso decaimiento distorsión',
  distortionDecayRate: 'Velocidad decaimiento distorsión',
  distortionWarningRatio: 'Ratio aviso distorsión',
  price: 'Precio',
  aUEC: 'Precio (aUEC)',
  basePrice: 'Precio base',
};

const DISTORTION_HINTS: Array<[RegExp, string]> = [
  [/shutdown.*(dmg|damage)|distortion.*shutdown/i, 'Daño apagado distorsión'],
  [/decay.*delay|delay.*distortion/i, 'Retraso decaimiento distorsión'],
  [/decay.*rate|distortion.*rate/i, 'Velocidad decaimiento distorsión'],
  [/warning.*ratio|distortion.*warning/i, 'Ratio aviso distorsión'],
];

const labelForStatKey = (key: string): string => {
  if (STAT_LABELS[key]) return STAT_LABELS[key];
  for (const [re, label] of DISTORTION_HINTS) {
    if (re.test(key)) return label;
  }
  return humanizeKey(key);
};

const flattenByTypeRemaining = (
  byType: Record<string, unknown>,
  skipRootsCanonical: string[]
): PhysicalComponentRow[] => {
  const skipNorm = new Set(skipRootsCanonical.map(normKey));
  const rows: PhysicalComponentRow[] = [];
  const walk = (obj: Record<string, unknown>, prefix: string) => {
    Object.entries(obj).forEach(([k, v]) => {
      if (isInternalKey(k)) return;
      const path = prefix ? `${prefix}.${k}` : k;
      const root = path.split('.')[0];
      if (!prefix && skipNorm.has(normKey(root))) return;
      if (v === undefined || v === null) return;
      if (typeof v === 'object' && !Array.isArray(v)) {
        walk(v as Record<string, unknown>, path);
      } else {
        const fs = formatValue(v);
        if (!fs) return;
        const parts = path.split('.');
        const label =
          parts.length <= 2
            ? parts.map((p) => humanizeKey(p)).join(' · ')
            : `${humanizeKey(parts[parts.length - 2])} · ${humanizeKey(parts[parts.length - 1])}`;
        rows.push({ id: `bytype-${path}`, label, value: fs });
      }
    });
  };
  walk(byType, '');
  return rows;
};

/**
 * Secciones ordenadas para ficha de componente físico (radares y el resto con ui.stats / byType).
 */
export function buildPhysicalComponentSections(component: PhysicalComponent): PhysicalComponentSection[] {
  const summary = asRecord(component.ui?.summary);
  const stats = asRecord(component.ui?.stats);
  const byType = asRecord(component.ui?.byType);
  const raw = asRecord(component.raw);

  const ident: PhysicalComponentRow[] = [];
  const add = (rows: PhysicalComponentRow[], id: string, label: string, value: unknown) => {
    const fs = formatValue(value);
    if (!fs) return;
    if (rows.some((r) => r.id === id)) return;
    rows.push({ id, label, value: fs });
  };

  add(ident, 'manufacturer', 'Fabricante', firstDefined(summary.manufacturer, component.manufacturer));
  add(
    ident,
    'class',
    'Clase',
    firstDefined(
      summary.class,
      summary.itemClass,
      summary.vehicleClass,
      summary.componentClass,
      pickRaw(raw, ['Class', 'class', 'VehicleClass', 'vehicleClass', 'ItemClass'])
    )
  );
  add(ident, 'grade', 'Grado', firstDefined(summary.grade, component.grade));
  add(ident, 'size', 'Tamaño', firstDefined(summary.size, component.size));
  add(ident, 'type', 'Tipo', firstDefined(summary.type, component.type));
  add(ident, 'subType', 'Subtipo', firstDefined(summary.subType));

  const aim: PhysicalComponentRow[] = [];
  const aimAssist = getByTypeSection(byType, 'aimAssist');
  add(aim, 'aim-min', 'Aim assist dist. mín.', aimAssist.distanceMinAssignment);
  add(aim, 'aim-max', 'Aim assist dist. máx.', aimAssist.distanceMaxAssignment);
  add(aim, 'aim-buffer', 'Buffer fuera de rango', aimAssist.outsideRangeBufferDistance);

  const sens: PhysicalComponentRow[] = [];
  const radarSens = getByTypeSection(byType, 'radarSensitivity');
  add(sens, 'sens-cs', 'Sensibilidad CS', bandSensitivity(radarSens, 'cs'));
  add(sens, 'sens-em', 'Sensibilidad EM', bandSensitivity(radarSens, 'em'));
  add(sens, 'sens-ir', 'Sensibilidad IR', bandSensitivity(radarSens, 'ir'));
  add(sens, 'sens-rs', 'Sensibilidad RS', bandSensitivity(radarSens, 'rs'));

  const statRows: PhysicalComponentRow[] = [];
  const usedStatKeys = new Set<string>();

  const statPriority = [
    'powerConsumption',
    'power',
    'mass',
    'coolingConsumption',
    'emSignature',
    'irSignature',
    'health',
    'distortionMax',
    'distortionShutdownDamage',
    'distortionDecayDelay',
    'distortionDecayRate',
    'distortionWarningRatio',
    'price',
    'aUEC',
  ];

  for (const key of statPriority) {
    if (stats[key] === undefined || stats[key] === null) continue;
    add(statRows, `stat-${key}`, labelForStatKey(key), stats[key]);
    usedStatKeys.add(key);
  }

  for (const [key, value] of Object.entries(stats)) {
    if (usedStatKeys.has(key) || isInternalKey(key)) continue;
    if (value === undefined || value === null) continue;
    if (typeof value === 'object') continue;
    add(statRows, `stat-${key}`, labelForStatKey(key), value);
    usedStatKeys.add(key);
  }

  if (!usedStatKeys.has('price') && !statRows.some((r) => r.id === 'stat-price')) {
    add(
      statRows,
      'stat-price-fallback',
      'Precio',
      firstDefined(summary.price, pickRaw(raw, ['price', 'Price', 'aUEC', 'Auec', 'basePrice', 'BasePrice']))
    );
  }

  for (const [key, value] of Object.entries(raw)) {
    if (isInternalKey(key)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    if (!/distortion|decay|shutdown|warning|damage.*threshold|dmg.*distortion/i.test(key)) continue;
    const id = `raw-stat-${key}`;
    if (statRows.some((r) => r.id === id || r.id === `stat-${key}`)) continue;
    add(statRows, id, labelForStatKey(key), value);
  }

  const sections: PhysicalComponentSection[] = [];
  if (ident.length) sections.push({ title: 'Identificación', rows: ident });
  if (aim.length) sections.push({ title: 'Asistencia de puntería', rows: aim });
  if (sens.length) sections.push({ title: 'Sensibilidades radar', rows: sens });
  if (statRows.length) sections.push({ title: 'Rendimiento, firma y economía', rows: statRows });

  const extraByType = flattenByTypeRemaining(byType, ['aimAssist', 'radarSensitivity']);
  if (extraByType.length) {
    sections.push({ title: 'Específico por tipo (otros)', rows: extraByType });
  }

  return sections;
}
